import React, { useEffect, useRef, useCallback } from 'react';
import styles from './TaskDetailModal.module.css';
import { FaTimes, FaUser, FaCalendarAlt, FaClock, FaExclamationCircle, FaCheck } from 'react-icons/fa';
import { useTheme } from '../../contexts/ThemeContext';

const PRIORITY_CONFIG = {
  high: { label: 'Yüksek', color: '#e53e3e' },
  medium: { label: 'Orta', color: '#dd6b20' },
  low: { label: 'Düşük', color: '#38a169' }
};

const STATUS_CONFIG = {
  pending: { label: 'Beklemede', color: '#718096' },
  'in-progress': { label: 'Devam Ediyor', color: '#3182ce' },
  completed: { label: 'Tamamlandı', color: '#38a169' },
  cancelled: { label: 'İptal Edildi', color: '#e53e3e' }
};

const TaskDetailModal = ({ task, onClose, onStatusChange, isAdmin, employeeId }) => {
  const { isDarkMode } = useTheme();
  const modalRef = useRef(null);

  const handleOverlayClick = useCallback((e) => {
    if (e.target === modalRef.current) onClose();
  }, [onClose]);

  useEffect(() => {
    const handleEscape = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleEscape);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [onClose]);

  const formatDate = (timestamp) => {
    if (!timestamp) return '-';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('tr-TR', {
      day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const formatShortDate = (timestamp) => {
    if (!timestamp) return '-';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const isOverdue = () => {
    if (!task.dueDate || task.status === 'completed' || task.status === 'cancelled') return false;
    const due = task.dueDate.toDate ? task.dueDate.toDate() : new Date(task.dueDate);
    return due < new Date();
  };

  const canChangeStatus = isAdmin || task.assigneeId === employeeId ||
    (task.assignees && task.assignees.some(a => a.id === employeeId));

  return (
    <div className={`${styles.overlay} ${isDarkMode ? styles.dark : ''}`} ref={modalRef} onClick={handleOverlayClick}>
      <div className={styles.modal}>
        {/* Header */}
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>Görev Detayı</h2>
          <button className={styles.closeBtn} onClick={onClose}>
            <FaTimes />
          </button>
        </div>

        {/* Body */}
        <div className={styles.modalBody}>
          {/* Title & Priority */}
          <div className={styles.titleSection}>
            <h3 className={styles.taskTitle}>{task.title}</h3>
            <div className={styles.badgeRow}>
              <span className={styles.badge} style={{ color: PRIORITY_CONFIG[task.priority]?.color, borderColor: PRIORITY_CONFIG[task.priority]?.color }}>
                {PRIORITY_CONFIG[task.priority]?.label} Öncelik
              </span>
              <span className={styles.badge} style={{ color: STATUS_CONFIG[task.status]?.color, borderColor: STATUS_CONFIG[task.status]?.color }}>
                {STATUS_CONFIG[task.status]?.label}
              </span>
              {isOverdue() && (
                <span className={styles.overdueBadge}>Gecikmiş</span>
              )}
            </div>
          </div>

          {/* Description */}
          {task.description && (
            <div className={styles.section}>
              <h4 className={styles.sectionTitle}>Açıklama</h4>
              <p className={styles.description}>{task.description}</p>
            </div>
          )}

          {/* Info Grid */}
          <div className={styles.infoGrid}>
            <div className={styles.infoItem}>
              <FaUser className={styles.infoIcon} />
              <div>
                <span className={styles.infoLabel}>Atanan</span>
                <span className={styles.infoValue}>
                  {task.assignees && task.assignees.length > 0
                    ? task.assignees.map(a => a.name).join(', ')
                    : task.assigneeName || '-'}
                </span>
              </div>
            </div>
            <div className={styles.infoItem}>
              <FaCalendarAlt className={styles.infoIcon} />
              <div>
                <span className={styles.infoLabel}>Son Tarih</span>
                <span className={`${styles.infoValue} ${isOverdue() ? styles.overdueText : ''}`}>
                  {formatShortDate(task.dueDate)}
                </span>
              </div>
            </div>
            <div className={styles.infoItem}>
              <FaClock className={styles.infoIcon} />
              <div>
                <span className={styles.infoLabel}>Oluşturulma</span>
                <span className={styles.infoValue}>{formatDate(task.createdAt)}</span>
              </div>
            </div>
            {task.createdBy && (
              <div className={styles.infoItem}>
                <FaUser className={styles.infoIcon} />
                <div>
                  <span className={styles.infoLabel}>Oluşturan</span>
                  <span className={styles.infoValue}>{task.createdBy}</span>
                </div>
              </div>
            )}
            {task.completedAt && (
              <div className={styles.infoItem}>
                <FaCheck className={styles.infoIcon} style={{ color: '#38a169' }} />
                <div>
                  <span className={styles.infoLabel}>Tamamlanma</span>
                  <span className={styles.infoValue}>{formatDate(task.completedAt)}</span>
                </div>
              </div>
            )}
          </div>

          {/* Status Change */}
          {canChangeStatus && (
            <div className={styles.statusSection}>
              <h4 className={styles.sectionTitle}>Durumu Değiştir</h4>
              <div className={styles.statusButtons}>
                {Object.entries(STATUS_CONFIG).map(([key, val]) => (
                  <button
                    key={key}
                    className={`${styles.statusBtn} ${task.status === key ? styles.statusBtnActive : ''}`}
                    style={{
                      borderColor: val.color,
                      color: task.status === key ? '#fff' : val.color,
                      backgroundColor: task.status === key ? val.color : 'transparent'
                    }}
                    onClick={() => {
                      onStatusChange(task.id, key);
                      onClose();
                    }}
                  >
                    {val.label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TaskDetailModal;
